import React, {useEffect, useRef, useState} from "react";
import {axiosInstance} from "../../Api/AxiosConfig";
import {Waiting} from "../../Components/System/Waiting";
import {ChevronRightIcon, ChevronUpIcon} from "@heroicons/react/24/solid";
import {PurchaseTypePill} from "../../Components/Information/PurchaseTypePill";
import {formatNumberWithSeparator} from "../../Hooks/UseGenericFunctions";
import {VATIcon} from "../../Components/Cars/VATIcon";
import {CheckCircleIcon, ChevronLeftIcon} from "@heroicons/react/24/outline";
import {EquipmentIcons} from "../../Components/Equipment/EquipmentIcons";
import {ChevronDownIcon} from "@heroicons/react/24/outline/index.js";
import {OfferInput} from "../Presales/OfferInput";
import {OverlayClose} from "../../Components/Navigation/OverlayClose";
import {AddToBasketButton, SizeEnum} from "../../Components/ShoppingBasket/AddToBasketButton";
import {DirectionEnum} from "../../Enums/DirectionEnum";
import {CarzelleTooltip} from "../../Components/System/Messages/CarzelleTooltip";

const SectionHeader = ({title}) => (
    <div className="text-2xl font-bold mb-4 border-b pb-4">
        {title}
    </div>
);

const CarAttribute = ({label, value}) => (
    <div className="flex items-start justify-between w-full mb-2">
        <div className="font-semibold whitespace-nowrap">{label}</div>
        <div className="text-gray-500 flex justify-end text-right">{value}</div>
    </div>
);

export const ShowroomCarView = ({car, hidePrices, equipmentBaseIcons, changeCar, close, fnUpdateData}) => {
    const [carDetails, setCarDetails] = useState();
    const [selectedImage, setSelectedImage] = useState(0);
    const [isBottom, setIsBottom] = useState(false);
    const [offerButtonVisible, setOfferButtonVisible] = useState(true);
    const scrollContainerRef = useRef(null);

    const scrollToBottom = () => {
        if (scrollContainerRef.current) {
            const div = scrollContainerRef.current;
            div.scrollTop = div.scrollHeight;
        }
    }

    const scrollToTop = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
            setIsBottom(false);
        }
    }

    useEffect(() => {
        if (car) {
            axiosInstance
                .get(car.info_endpoint)

                .then((response) => {
                    setCarDetails(response.data);
                    setSelectedImage(0);
                })
                .catch((error) => {
                    console.error("Error fetching data:", error);
                });
        }
    }, [car]);

    useEffect(() => {
        if (car) {
            setOfferButtonVisible(car.offer_amount_confirmed === null && car.fixed_price === null && !car.in_basket);
        }
    }, [car]);


    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const {scrollTop, scrollHeight, clientHeight} = scrollContainerRef.current;
            setIsBottom(scrollTop + clientHeight >= scrollHeight - 5); // 5px threshold to handle rounding
        }
    };

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        scrollToTop();
        if (scrollContainer) {
            scrollContainer.addEventListener("scroll", handleScroll);
        }
        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener("scroll", handleScroll);
            }
        };
    }, []);

    if (carDetails && car) {
        const midEquipmentIndex = Math.ceil(carDetails.equipment.length / 2);
        const leftEquipmentColumn = carDetails.equipment.slice(0, midEquipmentIndex);
        const rightEquipmentColumn = carDetails.equipment.slice(midEquipmentIndex);

        return (
            <div className="flex flex-col h-screen relative">
                <div className="sticky top-0 left-0 p-8 bg-white z-50">
                    <OverlayClose close={close} className="text-black top-4 right-4 z-[00]"/>
                    <CarzelleTooltip message="You can also use the arrow keys to navigate" withArrow={true} openDelay={1500} transition='pop'>
                        <div className="relative flex items-center text-gray-400 mb-4 cursor-pointer w-max">
                            <div className="flex items-center" onClick={() => changeCar(DirectionEnum.previous)}>
                                <div className="mr-2 rounded-full bg-gray-100 p-2">
                                    <ChevronLeftIcon className="w-5 h-5 "/>
                                </div>
                                <div className="mr-4 hover:underline">Previous</div>
                            </div>
                            <div className="flex items-center" onClick={() => changeCar(DirectionEnum.next)}>
                                <div className="mr-2 hover:underline">Next</div>
                                <div className="rounded-full bg-gray-100 p-2">
                                    <ChevronRightIcon className="w-5 h-5 "/>
                                </div>
                            </div>
                        </div>
                    </CarzelleTooltip>
                </div>
                <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-8" onScroll={handleScroll}>
                    {carDetails.images.all.length > 0 &&
                        <div className="flex items-start justify-between">
                            <div className="relative max-w-[1020px] h-[500px] w-full">
                                <img className='object-cover rounded-xl w-[750px] max-h-[500px] h-[500px] ' src={carDetails.images.all[selectedImage].file_url} alt={'image for ' + car.designation}/>
                                {car.equipment_base_icons !== null &&
                                    <div className="absolute z-50 left-2 bottom-6 rounded-xl bg-white px-4 py-1">
                                        <EquipmentIcons carId={car.id} equipment={car.equipment_base_icons} equipmentBaseIcons={equipmentBaseIcons} size="24px" opacity={0.3}/>
                                    </div>
                                }
                            </div>
                            <div className="w-[230px] max-h-[500px] overflow-y-auto">
                                {carDetails.images.all.filter(image => !image.damage).map((image, index) => (
                                    <img key={index} className='cursor-pointer object-cover rounded-xl w-full mb-4' loading="lazy" src={image.thumb_file_url} alt={'image for ' + car.designation} onClick={() => setSelectedImage(index)}/>
                                ))}
                            </div>
                        </div>
                    }

                    {carDetails.images.all.length === 0 &&
                        <div className="flex items-start justify-center">
                            <div className="relative max-w-[1020px] h-[500px]">
                                <img className='object-cover rounded-xl w-[750px] max-h-[500px] h-[500px] border ' src={carDetails.images.logo} alt={'image for ' + car.designation}/>
                                {car.equipment_base_icons !== null &&
                                    <div className="absolute z-50 left-2 bottom-6 rounded-xl bg-white border px-4 py-1">
                                        <EquipmentIcons carId={car.id} equipment={car.equipment_base_icons} equipmentBaseIcons={equipmentBaseIcons} size="24px" opacity={0.3}/>
                                    </div>
                                }
                            </div>
                        </div>
                    }

                    <div className="flex items-center mb-5 mt-8 ">
                        <div className="text-3xl font-semibold mr-4">
                            {car.designation}
                        </div>
                        <div>
                            <PurchaseTypePill car={car} className="rounded-2xl px-2"/>
                        </div>
                    </div>

                    <div className="flex items-start justify-between">
                        <div className="mt-4 w-full">
                            <div className="flex items-start justify-between">
                                <div className="w-[250px]">
                                    <CarAttribute label="Car #" value={car.car_id}/>
                                    <CarAttribute label="First reg." value={carDetails.cars.reg_date.value}/>
                                    <CarAttribute label="Model year" value={carDetails.cars.model_year.value}/>
                                    <CarAttribute label="KM" value={formatNumberWithSeparator(parseInt(car.km), '.')}/>
                                    <CarAttribute label="Color" value={car.color}/>
                                    <CarAttribute label="Damage" value={carDetails.cars.damage_amount.value}/>
                                </div>
                                <div className="w-[250px]">
                                    <CarAttribute label="VIN" value={car.vin_no}/>
                                    <CarAttribute label="Fuel" value={carDetails.cars.fuel_name.value}/>
                                    <CarAttribute label="Gear" value={carDetails.cars.gear_name.value}/>
                                    <CarAttribute label="HP" value={carDetails.cars.horsepower.value}/>
                                    <CarAttribute label="Seats" value={carDetails.cars.seats.value}/>
                                    {carDetails.cars.battery_size.value !== null && <CarAttribute label='Battery size' value={carDetails.cars.battery_size.value}/>}
                                </div>
                                <div className="w-[400px]">
                                    <div className={`bg-white shadow-2xl rounded-2xl p-8 ${hidePrices ? 'hidden' : ''}`}>
                                        <div className="text-gray-500">Pris</div>
                                        <div className="text-3xl font-semibold flex items-center">{formatNumberWithSeparator(parseInt(car.price), '.')}&nbsp;{car.currency}&nbsp;<VATIcon vatStatusId={car.vat_status_id}/></div>
                                        <div className="border-t mt-4 mb-4"></div>
                                        <div>
                                            <AddToBasketButton car={car} fnUpdate={() => {
                                                fnUpdateData();
                                                close()
                                            }} size={SizeEnum.Big} bgColor={`${car.allow_offers || car.buy_status !== 'listing' ? 'bg-purchase-color' : 'bg-presales'} `} carInBasket={car.in_basket} tooltip="You can add this car to your list for consideration, including it to those you want us to acquire for you."/>
                                        </div>
                                        <div className="mt-2">
                                            {offerButtonVisible &&
                                                <OfferInput car={car} className="h-[60px]" fnUpdate={() => {
                                                    fnUpdateData();
                                                    close()
                                                }} inputClass="text-lg" idPrefix="cardOffer" showSubmitButton={true}/>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <SectionHeader title="Equipment"/>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    {leftEquipmentColumn.map((equipment, index) => (
                                        <div key={"equipment_" + index} className="flex items-start mb-2">
                                            <div><CheckCircleIcon className="w-8 h-8 text-gray-600 mr-4" strokeWidth={0.8}/></div>
                                            <div className="">{equipment.name}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="space-y-2">
                                    {rightEquipmentColumn.map((equipment, index) => (
                                        <div key={"equipment_" + index} className="flex items-start mb-2">
                                            <div><CheckCircleIcon className="w-8 h-8 text-gray-600 mr-4" strokeWidth={0.8}/></div>
                                            <div className="">{equipment.name}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="sticky bottom-0 left-0 w-full cursor-pointer flex items-center justify-center bg-white py-2 opacity-75 z-50" onClick={(e) => {if (isBottom) {scrollToTop()} else {scrollToBottom(e)}}}>
                    {!isBottom && <ChevronDownIcon className="w-8 h-8 "/>}
                    {isBottom && <ChevronUpIcon className="w-8 h-8 "/>}
                </div>
            </div>
        )
    } else {
        return (
            <Waiting/>
        )
    }
}